// API Configuration for frontend
export const API_CONFIG = {
  // Base URL for API calls
  BASE_URL: import.meta.env.VITE_API_URL || 'http://localhost:3002/api/v1',

  // App URL for redirects and navigation
  APP_URL: import.meta.env.VITE_APP_URL || 'http://localhost:3000',

  // API endpoints
  ENDPOINTS: {
    // Authentication
    AUTH: {
      LOGIN: '/auth/login',
      REGISTER: '/auth/register',
      LOGOUT: '/auth/logout',
      REFRESH: '/auth/refresh',
      ME: '/auth/me',
      VERIFY_EMAIL: '/auth/verify-email',
      RESEND_VERIFICATION: '/auth/resend-verification',
      FORGOT_PASSWORD: '/auth/forgot-password',
      RESET_PASSWORD: '/auth/reset-password',
      CHANGE_PASSWORD: '/auth/change-password',
      CUSTOMER_LOGIN: '/auth/customer-login',
      LOGIN_UNIFIED: '/auth/login-unified',
      TENANT_LOGIN: '/auth/login-tenant',
      CUSTOMER_REGISTER: '/auth/customer-register',
      SOCIAL_PROVIDERS: '/auth/social/providers',
      SOCIAL_CONNECTIONS: '/auth/social/connections',
      SOCIAL_UNLINK: '/auth/social/unlink',
    },

    // Platform Admin
    PLATFORM: {
      TENANTS: '/platform/tenants',
      USERS: '/platform/users',
      ANALYTICS: '/platform/analytics',
      SETTINGS: '/platform/settings',
      HEALTH: '/platform/health',
    },

    // Staff / tenant users
    USERS: {
      STAFF: '/users/staff',
      STAFF_STATS: '/users/staff/stats',
      STAFF_AUDIT: '/users/staff/audit',
      STAFF_BY_ID: (id: string) => `/users/staff/${id}`,
    },

    // Customer Management
    CUSTOMERS: {
      LIST: '/customers',
      CREATE: '/customers',
      UPDATE: (id: string) => `/customers/${id}`,
      DELETE: (id: string) => `/customers/${id}`,
      DETAILS: (id: string) => `/customers/${id}`,
      STATS: '/customers/stats',
      SEARCH: '/customers/search',
      SEARCH_DUPLICATES: '/customers/search-duplicates',
      EDIT_LOCK: (id: string) => `/customers/${id}/edit-lock`,
      PROFILE_AUDIT: (id: string) => `/customers/${id}/profile-audit`,
      PROFILE_AUDIT_TENANT: '/customers/profile-audit',
      PROFILE_AUDIT_EXPORT: (id: string) => `/customers/${id}/profile-audit/export`,
      MEDICAL: (id: string) => `/customers/${id}/medical`,
      MEDICAL_SUMMARY: (id: string) => `/customers/${id}/medical/summary`,
      MEDICAL_AUDIT: (id: string) => `/customers/${id}/medical/audit`,
      MEDICAL_CONTRAINDICATIONS: (id: string, treatmentId: string) =>
        `/customers/${id}/medical/contraindications?treatmentId=${encodeURIComponent(treatmentId)}`,
      MEDICAL_ALTERNATIVES: (id: string, treatmentId: string) =>
        `/customers/${id}/medical/alternatives?treatmentId=${encodeURIComponent(treatmentId)}`,
      MEDICAL_CONDITIONS: '/customers/medical-conditions',
      NOTES: (id: string) => `/customers/${id}/notes`,
      NOTE: (id: string, noteId: string) => `/customers/${id}/notes/${noteId}`,
    },

    // Bookings Management
    BOOKINGS: {
      LIST: '/bookings',
      CREATE: '/bookings',
      UPDATE: (id: string) => `/bookings/${id}`,
      DELETE: (id: string) => `/bookings/${id}`,
      DETAILS: (id: string) => `/bookings/${id}`,
      STATS: '/bookings/stats',
      SEARCH: '/bookings/search',
      NOTES: (id: string) => `/bookings/${id}/notes`,
      NOTE: (id: string, noteId: string) => `/bookings/${id}/notes/${noteId}`,
    },

    // Spa Packages Management
    SPA_PACKAGES: {
      LIST: '/spa-packages',
      CREATE: '/spa-packages',
      UPDATE: (id: string) => `/spa-packages/${id}`,
      DELETE: (id: string) => `/spa-packages/${id}`,
      DETAILS: (id: string) => `/spa-packages/${id}`,
      STATS: '/spa-packages/stats',
    },

    // Treatment Types Management
    TREATMENT_TYPES: {
      LIST: '/treatment-types',
      CREATE: '/treatment-types',
      UPDATE: (id: string) => `/treatment-types/${id}`,
      DELETE: (id: string) => `/treatment-types/${id}`,
      DETAILS: (id: string) => `/treatment-types/${id}`,
      STATS: '/treatment-types/stats',
    },

    // Vouchers Management
    VOUCHERS: {
      LIST: '/vouchers',
      CREATE: '/vouchers',
      UPDATE: (id: string) => `/vouchers/${id}`,
      DELETE: (id: string) => `/vouchers/${id}`,
      DETAILS: (id: string) => `/vouchers/${id}`,
      STATS: '/vouchers/stats',
      VALIDATE: '/vouchers/validate',
    },

    // Orders Management
    ORDERS: {
      LIST: '/orders',
      CREATE: '/orders',
      UPDATE: (id: string) => `/orders/${id}`,
      DELETE: (id: string) => `/orders/${id}`,
      DETAILS: (id: string) => `/orders/${id}`,
      STATS: '/orders/stats',
      ADD_PAYMENT: (id: string) => `/orders/${id}/payments`,
    },

    // Payment Methods Management
    PAYMENTS: {
      METHODS: '/payments/methods',
      METHOD: (id: string) => `/payments/methods/${id}`,
      SET_DEFAULT: (id: string) => `/payments/methods/${id}/default`,
    },

    // Sessions management
    SESSIONS: {
      LIST: '/sessions',
      HORIZON: '/sessions/horizon',
    },

    // Additional endpoints can be added here as needed
  },

  // Helper function to build full API URLs
  buildUrl: (endpoint: string) => {
    // Absolute URLs are used as-is (e.g. external or already-resolved download links)
    if (/^https?:\/\//i.test(endpoint)) {
      return endpoint;
    }

    const baseUrl = API_CONFIG.BASE_URL.endsWith('/')
      ? API_CONFIG.BASE_URL.slice(0, -1)
      : API_CONFIG.BASE_URL;
    let cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

    // Backend sometimes returns paths already prefixed with /api/v1 while BASE_URL
    // also ends in /api/v1 — strip the duplicate to avoid /api/v1/api/v1/...
    if (baseUrl.endsWith('/api/v1') && cleanEndpoint.startsWith('/api/v1/')) {
      cleanEndpoint = cleanEndpoint.slice('/api/v1'.length);
    }

    return `${baseUrl}${cleanEndpoint}`;
  },

  // Default request options
  DEFAULT_OPTIONS: {
    headers: {
      'Content-Type': 'application/json',
    },
  },
};

// Export authenticated request function for direct import
export const authenticatedRequest = async (endpoint: string, options?: RequestInit) => {
  return api.authenticatedRequest(endpoint, options);
};

// Export convenience functions for common API operations
export const api = {
  // GET request helper
  get: async (endpoint: string, options?: RequestInit) => {
    const url = API_CONFIG.buildUrl(endpoint);
    const response = await fetch(url, {
      method: 'GET',
      ...API_CONFIG.DEFAULT_OPTIONS,
      ...options,
    });
    return response;
  },

  // POST request helper
  post: async (endpoint: string, data?: any, options?: RequestInit) => {
    const url = API_CONFIG.buildUrl(endpoint);
    const requestOptions: RequestInit = {
      method: 'POST',
      headers: {
        ...API_CONFIG.DEFAULT_OPTIONS.headers,
        ...options?.headers,
      },
      body: data ? JSON.stringify(data) : undefined,
    };

    // Apply any other options that don't conflict
    if (options?.cache) requestOptions.cache = options.cache;
    if (options?.credentials) requestOptions.credentials = options.credentials;
    if (options?.integrity) requestOptions.integrity = options.integrity;
    if (options?.keepalive) requestOptions.keepalive = options.keepalive;
    if (options?.mode) requestOptions.mode = options.mode;
    if (options?.redirect) requestOptions.redirect = options.redirect;
    if (options?.referrer) requestOptions.referrer = options.referrer;
    if (options?.referrerPolicy) requestOptions.referrerPolicy = options.referrerPolicy;
    if (options?.signal) requestOptions.signal = options.signal;

    const response = await fetch(url, requestOptions);
    return response;
  },

  // PUT request helper
  put: async (endpoint: string, data?: any, options?: RequestInit) => {
    const url = API_CONFIG.buildUrl(endpoint);
    const response = await fetch(url, {
      method: 'PUT',
      ...API_CONFIG.DEFAULT_OPTIONS,
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });
    return response;
  },

  // DELETE request helper
  delete: async (endpoint: string, options?: RequestInit) => {
    const url = API_CONFIG.buildUrl(endpoint);
    const response = await fetch(url, {
      method: 'DELETE',
      ...API_CONFIG.DEFAULT_OPTIONS,
      ...options,
    });
    return response;
  },

  // Request with authentication helper
  authenticatedRequest: async (endpoint: string, options?: RequestInit) => {
    const { authStorage, redirectToLoginPage } = await import('./auth');
    const { getTenantHeaders } = await import('./tenant');
    // RL-020: activity for the idle timer is tracked from real user interaction
    // (mouse/keyboard/touch/scroll) in SessionTimeoutManager — NOT from API calls.
    // Counting API calls here would let background pollers (e.g. staff notifications,
    // every 30s) keep the session alive forever and defeat the automatic timeout.
    const userRole = authStorage.getUser()?.role;
    let token = authStorage.getAccessToken();

    if (!token) {
      redirectToLoginPage({ role: userRole, clearSession: false });
      throw new Error('No authentication token found - redirecting to login');
    }

    const makeRequest = async (authToken: string) => {
      const url = API_CONFIG.buildUrl(endpoint);
      const tenantHeaders = getTenantHeaders();
      const isFormDataBody = typeof FormData !== 'undefined' && options?.body instanceof FormData;
      const requestHeaders: Record<string, string> = {
        ...API_CONFIG.DEFAULT_OPTIONS.headers,
        Authorization: `Bearer ${authToken}`,
        ...tenantHeaders, // Add tenant headers automatically
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0',
        ...(options?.headers as Record<string, string> | undefined),
      };

      // Multipart uploads must omit Content-Type so the browser sets the boundary.
      if (isFormDataBody) {
        delete requestHeaders['Content-Type'];
      }

      const response = await fetch(url, {
        ...API_CONFIG.DEFAULT_OPTIONS,
        ...options,
        headers: requestHeaders,
      });

      // Handle error response
      if (!response.ok) {
        const errorText = await response.text();
        // Return a new response with the same body for further processing
        return new Response(errorText, {
          status: response.status,
          statusText: response.statusText,
          headers: response.headers
        });
      }

      return response;
    };

    // First attempt
    let response = await makeRequest(token);

    // 403 = permission denied — never logout. Some backends also mislabel business errors as 401.
    if (response.status === 401) {
      const { isSessionAuthFailure } = await import('./api-errors');
      let errorBody: { error?: { code?: string; message?: string }; message?: string } | null = null;
      try {
        errorBody = JSON.parse(await response.clone().text());
      } catch {
        errorBody = null;
      }

      if (!isSessionAuthFailure(401, errorBody)) {
        // Valid session, but request is not allowed / misconfigured — let the caller show an error
        return response;
      }

      console.warn('Authentication error (401), attempting refresh...');

      try {
        const { refreshAccessToken } = await import('./auth');
        const newToken = await refreshAccessToken();

        if (newToken) {
          console.log('Token refreshed successfully, retrying request...');
          response = await makeRequest(newToken);

          if (response.status === 401) {
            const retryBody = await response.clone().text().then((t) => {
              try {
                return JSON.parse(t);
              } catch {
                return null;
              }
            });
            if (!isSessionAuthFailure(401, retryBody)) {
              return response;
            }
            redirectToLoginPage({ role: userRole });
            throw new Error('Authentication expired - please login again');
          }
          // Non-401 after refresh (including 403): return to caller — do not logout
        } else {
          console.error('Token refresh failed - user needs to login again');
          redirectToLoginPage({ role: userRole });
          throw new Error('Authentication expired - please login again');
        }
      } catch (refreshError) {
        if (refreshError instanceof Error && refreshError.message.includes('Authentication expired')) {
          throw refreshError;
        }
        console.error('Token refresh failed:', refreshError);
        redirectToLoginPage({ role: userRole });
        throw new Error('Authentication expired - please login again');
      }
    }

    return response;
  },

  // Public request helper (no authentication, but includes tenant headers)
  publicRequest: async (endpoint: string, options?: RequestInit) => {
    const { getTenantHeaders } = await import('./tenant');
    const tenantHeaders = getTenantHeaders();
    const url = API_CONFIG.buildUrl(endpoint);
    const response = await fetch(url, {
      ...API_CONFIG.DEFAULT_OPTIONS,
      ...options,
      headers: {
        ...API_CONFIG.DEFAULT_OPTIONS.headers,
        ...tenantHeaders,
        ...options?.headers,
      },
    });
    return response;
  },

  // Booking Notes API methods (staff only)
  addBookingNote: async (bookingId: string, content: string) => {
    const response = await api.authenticatedRequest(
      API_CONFIG.ENDPOINTS.BOOKINGS.NOTES(bookingId),
      {
        method: 'POST',
        body: JSON.stringify({ content })
      }
    );
    return response;
  },

  editBookingNote: async (bookingId: string, noteId: string, content: string) => {
    const response = await api.authenticatedRequest(
      API_CONFIG.ENDPOINTS.BOOKINGS.NOTE(bookingId, noteId),
      {
        method: 'PATCH',
        body: JSON.stringify({ content })
      }
    );
    return response;
  },

  getBookingNotes: async (bookingId: string) => {
    const response = await api.authenticatedRequest(
      API_CONFIG.ENDPOINTS.BOOKINGS.NOTES(bookingId),
      {
        method: 'GET'
      }
    );
    return response;
  },

  // Customer Notes API methods (staff only)
  addCustomerNote: async (customerId: string, content: string) => {
    const response = await api.authenticatedRequest(
      API_CONFIG.ENDPOINTS.CUSTOMERS.NOTES(customerId),
      {
        method: 'POST',
        body: JSON.stringify({ content })
      }
    );
    return response;
  },

  editCustomerNote: async (customerId: string, noteId: string, content: string) => {
    const response = await api.authenticatedRequest(
      API_CONFIG.ENDPOINTS.CUSTOMERS.NOTE(customerId, noteId),
      {
        method: 'PATCH',
        body: JSON.stringify({ content })
      }
    );
    return response;
  },

  getCustomerNotes: async (customerId: string) => {
    const response = await api.authenticatedRequest(
      API_CONFIG.ENDPOINTS.CUSTOMERS.NOTES(customerId),
      {
        method: 'GET'
      }
    );
    return response;
  },
};
